import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "@/api";

// Sets up the 401 interceptor on the shared `api` instance. Must mount
// inside <BrowserRouter> so it can call useNavigate() for a soft (SPA)
// redirect — that's the whole point versus the previous module-level
// interceptor in api.js, which had to use window.location.assign and
// therefore forced a full page reload.
function ApiInterceptorSetup() {
  const navigate = useNavigate();

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Single-credential model post-PR2: only user_token exists.
          // Both regular sessions and admin sessions live on it.
          localStorage.removeItem("user_token");
          // Generic message — covers session-expired, token_version-bumped,
          // and any other 401 we might surface in future.
          sessionStorage.setItem("auth_message", "Please log in again.");
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [navigate]);

  return null;
}

// /register redirect — preserves the query string so legacy links like
// /register?email=foo or /register?event=evt-1&signup=play don't lose
// their params on the way to /login. <Navigate to="/login"> alone resets
// the search string.
function RegisterRedirect() {
  const location = useLocation();
  return <Navigate to={`/login${location.search}`} replace />;
}

// Old admin login is gone — bookmarks redirect to the unified /login with
// a one-off toast so the user understands what happened. Set the message
// in sessionStorage and let LoginPage's existing auth_message banner
// surface it (same path as the 401 interceptor uses).
function AdminLoginRedirect() {
  useEffect(() => {
    sessionStorage.setItem(
      "auth_message",
      "Admin login has moved — please sign in with your account email."
    );
  }, []);
  return <Navigate to="/login" replace />;
}
import { MaintenanceProvider, useMaintenance } from "@/context/MaintenanceContext";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAdmin, RequireSuperAdmin } from "@/components/RequireAdmin";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import EventDetailPage from "@/pages/EventDetailPage";
import SignupPage from "@/pages/SignupPage";
import LoginPage from "@/pages/LoginPage";
import VerifyMagicLinkPage from "@/pages/VerifyMagicLinkPage";
import MaintenancePage from "@/pages/MaintenancePage";
import ErrorBoundary from "@/components/ErrorBoundary";

// Admin, account, and marketing pages are split out of the main bundle so
// first-time visitors to / and /events/:id don't download the operator UI.
const CompleteProfilePage = lazy(() => import("@/pages/CompleteProfilePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ManageAdminsPage = lazy(() => import("@/pages/ManageAdminsPage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const AdminEventPage = lazy(() => import("@/pages/AdminEventPage"));
const AdminSettingsPage = lazy(() => import("@/pages/AdminSettingsPage"));
const AdminVenuesPage = lazy(() => import("@/pages/AdminVenuesPage"));
const AdminTeachersPage = lazy(() => import("@/pages/AdminTeachersPage"));
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const TeachersPage = lazy(() => import("@/pages/TeachersPage"));
const TeacherSubmitPage = lazy(() => import("@/pages/TeacherSubmitPage"));
const AdminTeacherListingsPage = lazy(() => import("@/pages/AdminTeacherListingsPage"));
const HostSubmitPage = lazy(() => import("@/pages/HostSubmitPage"));
const HostEventPage = lazy(() => import("@/pages/HostEventPage"));
const HostMyEventsPage = lazy(() => import("@/pages/HostMyEventsPage"));
const AdminHostEventsPage = lazy(() => import("@/pages/AdminHostEventsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

function PageFallback() {
  return (
    <div className="loading" style={{ padding: "3rem", textAlign: "center" }}>
      Loading...
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENT_VERSION = "2.0.1"; // Must match backend APP_VERSION

// Wrapper component to check maintenance mode
function MaintenanceWrapper({ children }) {
  const { maintenance, loading } = useMaintenance();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (loading) {
    return null;
  }

  if (!isAdminRoute && maintenance?.maintenance_mode) {
    return <MaintenancePage message={maintenance.message} expiresAt={maintenance.expires_at} />;
  }

  return children;
}

function AppRoutes() {
  return (
    <MaintenanceWrapper>
      <ApiInterceptorSetup />
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/events/:eventId" element={<EventDetailPage />} />
        <Route path="/events/:eventId/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Legacy /register route — old email links and bookmarks land
            on the unified /login page now. RegisterRedirect preserves
            the query string (?email=, ?event=, ?signup=, etc). */}
        <Route path="/register" element={<RegisterRedirect />} />
        {/* Legacy /signup bookmarks and marketing links → unified login. */}
        <Route path="/signup" element={<RegisterRedirect />} />
        <Route path="/auth/verify" element={<VerifyMagicLinkPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Legacy admin login route — bookmarks land on /login. The toast
            informs users the admin login page has moved. */}
        <Route path="/admin/login" element={<AdminLoginRedirect />} />
        <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
        <Route path="/admin/events/:eventId" element={<RequireAdmin><AdminEventPage /></RequireAdmin>} />
        <Route path="/admin/settings" element={<RequireAdmin><AdminSettingsPage /></RequireAdmin>} />
        <Route path="/admin/venues" element={<RequireAdmin><AdminVenuesPage /></RequireAdmin>} />
        <Route path="/admin/teachers" element={<RequireAdmin><AdminTeachersPage /></RequireAdmin>} />
        <Route path="/admin/teacher-listings" element={<RequireAdmin><AdminTeacherListingsPage /></RequireAdmin>} />
        <Route path="/admin/host-events" element={<RequireAdmin><AdminHostEventsPage /></RequireAdmin>} />
        <Route path="/admin/admins" element={<RequireSuperAdmin><ManageAdminsPage /></RequireSuperAdmin>} />
        <Route path="/teachers" element={<TeachersPage />} />
        <Route path="/teachers/submit" element={<TeacherSubmitPage />} />
        <Route path="/host/submit" element={<HostSubmitPage />} />
        <Route path="/host/my-events" element={<HostMyEventsPage />} />
        <Route path="/host/events/:eventId" element={<HostEventPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </MaintenanceWrapper>
  );
}

function App() {
  // Check for app updates on load
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/version`);
        const data = await response.json();
        if (data.version && data.version !== CURRENT_VERSION) {
          // New version available - force reload to get latest code
          window.location.reload(true);
        }
      } catch (error) {
        // version check is not critical — fail silently
      }
    };
    checkVersion();
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ErrorBoundary>
            <MaintenanceProvider>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </MaintenanceProvider>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster
          position="top-center"
          richColors
          closeButton={true}
          duration={5000}
          expand={true}
          gap={8}
          visibleToasts={4}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;